import { fetchImageSize, fetchUsers } from './api';
import xml2js from 'xml2js';
import React from 'react';

export function extractLeasingPropertyAddress(data) {
  // console.log('[DEBUG] extractLeasingPropertyAddress >> data', data);

  if (!data || !data.address || !data.address[0]) {
    console.log('DEBUG extractLeasingPropertyAddress >> Invalid data structure:', {
      hasData: !!data,
      hasAddress: data && !!data.address,
      hasAddressArray: data && data.address && !!data.address[0]
    });
    return "";
  }

  const addressData = data.address[0];
  let address = "";

  if (addressData.subNumber && addressData.subNumber[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.subNumber[0] + '/';
  }

  if (addressData.streetNumber && addressData.streetNumber[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.streetNumber[0] + ' ';
  }

  if (addressData.street && addressData.street[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.street[0] + ', ';
  }

  if (addressData.suburb && addressData.suburb[0] && addressData.suburb[0]._ && (!addressData.suburb[0].$ || addressData.suburb[0].$.display === 'yes')) {
    address += addressData.suburb[0]._ + ', ';
  }

  if (addressData.state && addressData.state[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.state[0] + ' ';
  }

  if (addressData.postcode && addressData.postcode[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.postcode[0] + ', ';
  }

  if (addressData.country && addressData.country[0] && (!addressData.$ || addressData.$.display === 'yes')) {
    address += addressData.country[0];
  }

  return address;
}

/**
 * Outputs:
 *  {
 *    name: 'H.PM Leasing',
 *    telephones: { 'default': number, 'mobile': number }
 *    email: 'h.pm@hoyee.com.au'
 *  }
 * @param data
 * @returns {{name: string, telephones: *[], email: string}}
 */
export function extractLeasingPropertyContact(data) {
  let contact = {
    name: null,
    telephones: {},
    email: null
  };

  if (!data.listingAgent || !data.listingAgent[0]) {
    return contact;
  }

  const agent = data.listingAgent[0];

  // Extract name
  if (agent.name && agent.name[0]) {
    contact.name = agent.name[0];
  }

  // Extract telephone numbers
  if (agent.telephone) {
    agent.telephone.forEach(tel => {
      if (typeof tel === 'string') {
        contact.telephones.default = tel;
      } else if (tel._ && tel.$ && tel.$.type) {
        contact.telephones[tel.$.type] = tel._;
      }
    });
  }

  // Extract email
  if (agent.email && agent.email[0]) {
    contact.email = agent.email[0];
  }

  return contact;
}

export function extractLeasingPropertyCarSpaces(data) {
  if (!data || !data.features || !data.features[0]) {
    return '';
  }

  if (data.features[0].garages && data.features[0].garages[0] !== '') {
    return data.features[0].garages[0];
  }
  if(data.features[0].carports && data.features[0].carports[0] !== '') {
    return data.features[0].carports[0];
  }
  if(data.features[0].carspaces && data.features[0].carspaces[0] !== '') {
    return data.features[0].carspaces[0];
  }

  return '';
}

export function extractLeasingPropertyRent(data) {
  if (!data || !data.rent || !data.rent[0] || data.rent[0]._ === '' || data.rent[0].$.display !== 'yes') {
    return 'Contact Agent';
  }

  let period = data.rent[0].$.period.toLowerCase();
  if(period === 'weekly') {
    period = 'week';
  } else if(period === 'monthly') {
    period = 'month';
  } else if (period === 'annually') {
    period = 'year';
  }

  return `$${data.rent[0]._} / ${period}`;
}

export function extractLeasingPropertyCategory(data) {
  if (!data || !data.category || !data.category[0]) {
    return 'Unknown';
  }

  return data.category[0].$.name;
}

/**
 * Cleans the image list for each lease property:
 *   1. Removes entries with no valid URL (null, empty, missing).
 *   2. Removes entries confirmed to exceed `size` MB via the size-check API.
 *      When the check is unavailable (404, no Content-Length), the image is kept.
 */
export async function filterLeasingImagesBySize(propertiesLeasing, size = 20) {
  for (const property of propertiesLeasing) {
    if (property.images && property.images[0] && Array.isArray(property.images[0].img)) {
      const filteredImages = [];
      for (const img of property.images[0].img) {
        // Rule 1: drop if no valid URL
        if (!img || !img.$ || !img.$.url || img.$.url.trim() === '') {
          continue;
        }
        // Rule 2: drop only when size is positively confirmed to exceed the limit
        const imageSize = await fetchImageSize(img.$.url);
        const sizeBytes = imageSize && imageSize.size;
        const confirmedOversize =
          typeof sizeBytes === 'number' &&
          sizeBytes > 0 &&
          sizeBytes > size * 1024 * 1024;
        if (!confirmedOversize) {
          filteredImages.push(img);
        }
      }
      property.images[0].img = filteredImages;
    }
  }

  return propertiesLeasing;
}


export const parseToHTML = (text) => {
  const html = text.split('\n\n').map(paragraph =>
    `<p>${paragraph.replace(/\n/g, '<br>')}</p>`).join('');

  return html;
}

/**
 * Generate proper HTML Element by give text.
 * @param text
 * @returns {Element}
 */
export const createMarkup = (text) => {
  return (
    <div dangerouslySetInnerHTML={{ __html: parseToHTML(text) }}/>
  )
};

/**
 * Lease FTP API returns paginated XML strings in `items`.
 * Sale APIs return JSON objects in `items` (with `contactStaff`).
 * Both shapes may omit `type`, so we must not use `!response.type` alone to pick the branch.
 */
function isLeaseXmlPayload(response) {
  return (
    response &&
    Array.isArray(response.items) &&
    response.items.length > 0 &&
    typeof response.items[0] === 'string'
  );
}

export const fetchProperties = async (action, params) => {
  // Initialize properties and fetchedUsers array
  const properties = [];
  const fetchedUsers = [];

  // Fetching users
  try {
    const response = await fetchUsers({ pagesize: 50 });
    const userItems = response && Array.isArray(response.items) ? response.items : [];
    fetchedUsers.push(...userItems);
  } catch (error) {
    console.log(error);
  }

  if (fetchedUsers.length === 0) {
    console.log('Do not have users');
  }

  try {
    let page = 1;

    while (page) {
      const finalParams = { page, ...params };
      const response = await action(finalParams);
      console.log('fetchProperties >> response', response);

      if (isLeaseXmlPayload(response)) {
        console.log('fetchProperties >> page', page, ' items', response.items);

        page = response.urls && response.urls.next ? page + 1 : null;

        if (response.items) {
          const data = response.items;
          // Parse each XML chunk sequentially. Reusing one Parser with concurrent
          // parseString callbacks corrupts output (often only the first property is valid).
          for (const xmlStr of data) {
            try {
              const result = await xml2js.parseStringPromise(xmlStr);
              if (!result.root || !result.root.document) {
                console.log('fetchProperties >> Missing root or document in XML result');
                continue;
              }

              for (const doc of result.root.document) {
                try {
                  let property;
                  if (doc.rental && doc.rental[0]) {
                    property = doc.rental[0];
                  } else if (doc.commercial && doc.commercial[0]) {
                    property = doc.commercial[0];
                  } else {
                    console.log('[DEBUG]fetchProperties >> Missing rental or commercial data in document', JSON.stringify(doc, null, 2));
                    continue;
                  }

                  const address = extractLeasingPropertyAddress(property);
                  const find = properties.find(p => extractLeasingPropertyAddress(p) === address);
                  if (!find) {
                    properties.push(property);
                  }
                } catch (docError) {
                  console.error('Error processing document:', docError);
                }
              }
            } catch (parseErr) {
              console.error('Error parsing XML:', parseErr);
            }
          }
        }
      } else {
        // Sales API: JSON items with contactStaff
        const rawItems = response && Array.isArray(response.items) ? response.items : [];
        const saleItems =
          fetchedUsers.length === 0
            ? []
            : rawItems.filter(
                (item) =>
                  item &&
                  typeof item === 'object' &&
                  Array.isArray(item.contactStaff) &&
                  item.contactStaff.some((staff) =>
                    fetchedUsers.some((user) => user.id === staff.id)
                  )
              );
        properties.push(...saleItems);
        page = response.urls && response.urls.next ? page + 1 : null;
      }

      console.log('fetchProperties >> page', page, properties);
    }

    return properties;
  } catch (error) {
    console.error('fetchProperties error:', error);
    throw error;
  }
};
